<template>
  <div class='wrapper-page'>
    <contact-form ref='contactForm' @success='loadContact()' />

    <div id='contact-details' class='container-fluid'>
      <h1 class='page-title'>
        {{ contact.name }}

        <a @click.prevent='$refs.contactForm.open(contact)' href='#' class='edit'>
          {{$t('contacts.show.edit')}}
        </a>
      </h1>

      <div class='contact-info'>
        <div class="company-details">
          <h5>
            {{ $t('contacts.show.company') }}
            <strong v-if='contact.company'>
              {{ contact.company }}
            </strong>
            <strong v-else>
              {{ $t('contacts.show.no_company_added') }}
            </strong>
          </h5>
          <h5>
            {{ $t('contacts.show.position') }}
            <strong v-if='contact.position'>
              {{ contact.position }}
            </strong>
            <strong v-else>
              {{ $t('contacts.show.no_position_added') }}
            </strong>
          </h5>
          <h5>
            {{ $t('contacts.show.company_address') }}
            <strong v-if='contact.address'>
              {{ contact.address }}
            </strong>
            <strong v-else>
              {{ $t('contacts.show.no_company_address_added') }}
            </strong>
          </h5>
        </div>
        <div class="contact-details">
          <h5>
            {{ $t('contacts.show.email') }}
            <strong v-if='contact.email'>
              {{ contact.email }}
            </strong>
            <strong class='red-text' v-else>
              {{ $t('contacts.show.no_email') }}
            </strong>
          </h5>
          <h5>
            {{ $t('contacts.show.language') }}
            <strong v-if="contact.locale === 'de'">
              {{ $t('contacts.edit_modal.german') }}
            </strong>
            <strong v-else>
              {{ $t('contacts.edit_modal.english') }}
            </strong>
          </h5>
          <h5>
            {{ $t('contacts.show.notes') }}
            <strong v-if='contact.notes'>
              {{ contact.notes }}
            </strong>
            <strong v-else>
              {{ $t('contacts.show.no_notes_added') }}
            </strong>
          </h5>
          <h5>
            {{ $t('contacts.show.communication_status') }}
            <router-link
              :data-tooltip='lastMessageAlt'
              :to="{name: 'contact_communications', params: { id: id }}"
              class='own-tooltip own-tooltip-right'>
              <img :src='lastMessageSrc'/>
            </router-link>
          </h5>
          <h5>
            {{ $t('contacts.show.files') }}
            <strong>{{ contactStats.records_count }}</strong>
          </h5>
        </div>

        <div class='contact-tags'>
          <h5 class='tags-dl'>
            {{ $t('contacts.show.tags') }}
          </h5>

          <div class='tags-cont'>
            <tag-manager :resource='contact' resourceType='contact'
                         v-on:reload-list='loadContact()'/>

            <div :class='tag.color' class='existing-tags-wrapper'
                 v-for='tag in contact.tags'  :key="tag.id">
              <div class='btn-group btn-group-toggle' data-toggle='buttons'>
                <label class='btn active'>
                  <router-link :to="{name: 'tag', params: { id: tag.id }}">
                    {{ tag.name }}
                  </router-link>
                </label>

                <label @click.prevent='removeConnectedTag(tag)' class='btn'>
                  <i class='fa fa-remove'></i>
                </label>
              </div>
            </div>
          </div>
        </div>
        <div class="float-left checklist-status">
          <h5>
            {{ $t('contacts.show.checklist_status') }}
          </h5>
          <checklist-status :completed-items-count='contactStats.checked_checklist_items' :items-count='contactStats.total_checklist_items'/>
        </div>
      </div>

      <div class='nav-tabs-wrapper'>
        <ul role='tablist' class='nav nav-tabs'>
          <li class='nav-item'>
            <router-link :to="{ name: 'contact_folders', params: { id: id } }" class="nav-link show">
              {{$t('contacts.show.folders')}}
            </router-link>
          </li>
          <li class='nav-item'>
            <router-link :to="{ name: 'contact_communications', params: { id: id } }" class="nav-link show">
              {{$t('contacts.show.communication')}}
            </router-link>
          </li>
          <li class='nav-item'>
            <router-link :to="{ name: 'contact_interactive_notes', params: { id: id } }" class="nav-link show active">
              {{$t('contacts.show.interactive_notes')}}
            </router-link>
          </li>
          <li class='nav-item'>
            <router-link :to="{ name: 'contact_emails', params: { id: id } }" class="nav-link show">
              {{$t('contacts.show.automated_emails')}}
            </router-link>
          </li>
        </ul>
      </div>

      <div class='tab-content'>
        <div class="tab-pane active text-center">
          <loading v-if="loading"/>
          <div class='inbox-messages-list' v-for="inbox in orderedInbox" :key="inbox.id" v-else-if="inboxes.length > 0 && !loading">
            <div class='contact-message-wrapper'>
              <div class='id-card-wrapper'>
                <div class='img-wrapper'>
                  <user-image v-if="Object.keys(inbox.from_user).length > 0" :user='inbox.from_user' />
                  <span v-else-if="Object.keys(inbox.from_contact).length > 0">
                    <div class="contact-image-wrapper rounded-circle">
                      <span>
                      {{ inbox.from_contact.name ? inbox.from_contact.name.substring(0, 1) : '' }}
                      </span>
                    </div>
                  </span>
                  <user-image v-else-if="Object.keys(inbox.to_user).length > 0" :user='inbox.to_user' />
                  <div v-else-if="Object.keys(inbox.to_contact).length > 0" class="contact-image-wrapper rounded-circle">
                    <span>
                    {{ inbox.to_contact.name ? inbox.to_contact.name.substring(0, 1) : '' }}
                    </span>
                  </div>
                </div>
                <strong v-if="Object.keys(inbox.from_user).length > 0">{{ inbox.from_user.full_name }}</strong>
                <strong v-else-if="Object.keys(inbox.from_contact).length > 0">{{ inbox.from_contact.name }}</strong>
                <strong v-else-if="Object.keys(inbox.to_user).length > 0">{{ inbox.to_user.full_name }}</strong>
                <strong v-else-if="Object.keys(inbox.to_contact).length > 0">{{ inbox.to_contact.name }}</strong>
              </div>
              <div class='message'>
                <div class='messages-list-item'>
                  <div class='time'>
                    {{ formatTime(inbox.created_at) }}
                    {{ formatDate(inbox.created_at) }}
                  </div>

                  <div>
                    <div class='interaction-message' v-html='inbox.body'></div>
                  </div>

                  <div v-if="inbox.record">
                    <router-link :to="{ name: 'record', params: { id: inbox.record.id, referrer: $route.fullPath } }">
                      {{ inbox.record.filename}}
                    </router-link>
                  </div>
                </div>
                <p class='status'>
                  {{inbox.status}}
                </p>
              </div>
            </div>
          </div>

          <div class="empty-list-wrapper m-t-20" v-if="Object.keys(inboxes).length === 0 && !loading">
            <img src="../assets/no_interactive_notes.svg"/>
            <p>{{$t('contacts.show.no_notes')}}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import _modal from '../components/_modal'
  import _loading from '../components/_loading'
  import _user_image from '../components/_user_image'
  import tag_manager from '../components/tags/_tags_manager'
  import checklistStatus from '../components/contacts/_checklist_status'

  import _contact_form from '../components/shared/contacts/_contact_form'

  export default {
    components: {
      modal: _modal,
      'user-image': _user_image,
      'tag-manager': tag_manager,
      'contact-form': _contact_form,
      'checklist-status': checklistStatus,
      'loading': _loading,
    },
    props: ['id'],
    created() {
      this.loadContact()
      this.loadResponses()
      this.store.sendMetrics({name: 'contact_communication', params: this.$route.params, path: this.$route.fullPath})
    },
    watch: {
      id() {
        this.loadContact()
        this.loadResponses()
      },
    },
    data() {
      return {
        modalContact: {},
        loading: true
      }
    },
    computed: {
      meta() {
        return this.state.meta || {}
      },
      contacts() {
        return this.lists.contacts || []
      },
      contactStats () {
        if (!this.stats.contacts)
          return {}

        return this.stats.contacts[0]
      },
      folderStats() {
        if (!this.stats.folders)
          return {}

        const stats = {}
        this.stats.folders.forEach(f => stats[f.id] = f)
        return stats
      },
      recordsCount() {
        return Object.values(this.folderStats).reduce((s, f) => s + f.records_count, 0)
      },
      checklistItems() {
        return Object.values(this.folderStats).reduce((s, f) => s + f.total_checklist_items, 0)
      },
      checkedItems() {
        return Object.values(this.folderStats).reduce((s, f) => s + f.checked_checklist_items, 0)
      },
      contact() {
        return this.state.contact || {}
      },
      inboxes() {
        return this.state.interactiveNotes || []
      },
      lastMessage() {
        return this.inboxes[this.inboxes.length - 1]
      },
      lastMessageSrc() {
        if (!this.lastMessage)
          return require('../assets/gray-env.png')

        switch (this.lastMessage.status) {
          case 'pending':
            return require('../assets/yellow-env.png')
          case 'failed':
            return require('../assets/red-env.png')
          case 'delivered':
          case 'opened':
            return require('../assets/green-env.png')
        }
      },
      lastMessageAlt() {
        if (!this.lastMessage)
          return this.$t('contacts.no_sent_message')

        const d = this.formatDate(this.lastMessage.created_at)
        const t = new Date(this.lastMessage.created_at).toLocaleTimeString(this.locale, { timeStyle: 'short' })
        switch (this.lastMessage.status) {
          case 'pending':
            return this.$t('contacts.sending_message', { date: d, time: t })
          case 'failed':
            return this.$t('contacts.message_not_sent', { date: d, time: t })
          case 'delivered':
          case 'opened':
            return this.$t('contacts.message_sent', { date: d, time: t })
        }
      },
      messages_org(){
        return this.state.messages_org || []
      },
      orderedInbox() {
        let list = []

        this.inboxes.forEach( (inbox) => {
          list.push({body: inbox.message.body, from_user: inbox.message.from_user||{}, to_user: inbox.to_user||{}, to_contact: inbox.to_contact||{}, from_contact: inbox.message.from_contact||{}, created_at: inbox.created_at, record: inbox.message.record})
          inbox.sent_message_replies.forEach( (reply) => {
            list.push({body: reply.body, from_user: reply.from_user||{}, from_contact: reply.from_contact||{}, to_user: {}, to_contact: {}, created_at: reply.created_at, record: reply.record||{}})
          })
        })
        return list.slice(0).sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      },
      documentResponses() {
        return this.state.documentResponses || []
      },
      uploadResponses() {
        return this.state.uploadResponses || []
      },
      versionRequests() {
        return this.state.versionRequests || []
      },
      versionResponses() {
        return this.state.versionResponses || []
      },
      documentRequests(){
        return this.state.documentRequests || []
      },
      interactiveNotes(){
        return this.state.interactiveNotes || []
      },
      recordsByDocResponseId() {
        return this.documentResponses.reduce((acc, item) => Object.assign(acc, { [item.id]: item.records }), {})
      },
      recordByVersionRequestId() {
        return this.versionRequests.reduce((acc, item) => Object.assign(acc, { [item.id]: item.old_record }), {})
      },
      recordsByUpResponseId() {
        return this.uploadResponses.reduce((acc, item) => Object.assign(acc, { [item.id]: item.records }), {})
      },
      versionResponseById() {
        return this.versionResponses.reduce((acc, item) => Object.assign(acc, { [item.id]: item }), {})
      },
      attachedRecordsByDocRequestId(){
        return this.documentRequests.reduce((acc, item) => Object.assign(acc, { [item.id]: item.attached_records }), {})
      }
    },
    methods: {
      loadContact() {
        this.store.getContact(this.$props.id).then(() =>  this.loading = false)
        this.statStore.getContacts({ 'filters[id]': this.$props.id })
      },
      loadResponses() {
        this.store.getOrgMessages(this.$props.id)
        this.store.getDocumentResponses(this.$props.id)
        this.store.getUploadResponses(this.$props.id)
        this.store.getVersionResponses(this.$props.id)
        this.store.getVersionRequests(this.$props.id)
        this.store.getDocumentRequests(this.$props.id)
        this.store.getInteractiveNotes(this.$props.id)
      },
      updateContact(event) {
        this.store.updateContact(this.contact.id, new FormData(event.target)).then(() => {
          this.loadContact()
          this.$refs.contactForm.close()
        })
      },
      removeConnectedTag(tag) {
        this.store.removeConnectedTag(tag.id, { contact_id: this.contact.id }).then(() => {
          this.store.sendMetrics({name: 'remove_tag_from_contact', params: this.$route.params, path: this.$route.fullPath})

          this.loadContact()
        })
      },
      interactionFolderLink(folder) {
        const link = this.$router.resolve({ name: 'folder', params: { id: folder.id } }).href

        return `
            <a href="${link}">
              ${folder.name}
            </a>
        `
      },
      interactionRecordLink(record) {
        const link = this.$router.resolve({ name: 'record', params: { id: record.id } }).href

        return `
            <a href="${link}">
              ${record.filename}
            </a>
        `
      },
    }
  }
</script>

<style lang='sass'>
  @import '../styles/checklists'
  @import '../styles/contact'

  .contact-image-wrapper
    width: 45px
    height: 45px
    line-height: 45px
    background: #56DAE2
    font-weight: bold
    text-align: center
    overflow: hidden
    display: block
    float: left
    color: #fff
    margin-right: 7px
    img
      vertical-align: top
      min-width: 100%
      min-height: 100%
      width: auto
      height: auto
    div
      border-radius: 50%
    &.org-pic
      background: #eee
      color: #000

</style>
